import React, { useMemo, useState } from "react";
import EmployeeBookingFilterBar from "./EmployeeBookingFilterBar"; // the filter bar you shared
import Loader from "../../../../Loader";

/**
 * Props:
 *  - bookingsLoading: boolean
 *  - bookingsError: Error | ApolloError | null
 *  - services: array of bookings
 *  - employeeData: { fullName?: string }
 *  - bookingFilter: {
 *      bookingId?: string,
 *      status?: string[] | null,
 *      queryStartDate?: string | null,
 *      queryEndDate?: string | null,
 *      verificationStatus?: string | null,
 *      selectedDatePreset?: string | null,
 *      customStart?: string | null,
 *      customEnd?: string | null
 *    }
 *  - onFilterChange: (nextFilter) => void
 */
const TotalServicesPanel = ({
  bookingsLoading,
  bookingsError,
  services = [],
  employeeData,
  bookingFilter,
  onFilterChange,
}) => {
  const [openBookingId, setOpenBookingId] = useState(null);

  const bookingIdFilter = bookingFilter?.bookingId || "";

  const toggleBooking = (id) => {
    setOpenBookingId((prev) => (prev === id ? null : id));
  };

  const handleBookingIdChange = (e) => {
    onFilterChange({
      ...bookingFilter,
      bookingId: e.target.value,
    });
  };

  const formatTime = (val) => {
    if (!val) return "--";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return "--";
    return d.toLocaleString(); // tweak formatting if needed
  };

  const filteredServices = useMemo(() => {
    const {
      bookingId = "",
      status: statusFilter,
      queryStartDate,
      queryEndDate,
      verificationStatus,
    } = bookingFilter || {};

    const idTerm = (bookingId || "").toLowerCase();

    const isWithinRange = (dateStr) => {
      if (!dateStr) return true;
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return true;

      if (queryStartDate) {
        const s = new Date(queryStartDate);
        if (d < s) return false;
      }
      if (queryEndDate) {
        const e = new Date(queryEndDate);
        if (d > e) return false;
      }
      return true;
    };

    return services.filter((service) => {
      console.log("FILTERING SERVICE:", service);
      const sBookingId = (service.bookingId || "").toLowerCase();
      const matchesId = idTerm ? sBookingId.includes(idTerm) : true;

      const matchesDate = isWithinRange(service.bookingDate);

      const matchesStatus =
        statusFilter && statusFilter.length
          ? statusFilter.includes(service.status)
          : true;

      const matchesVerification = verificationStatus
        ? (service.verificationStatus || "").toUpperCase() ===
          verificationStatus
        : true;

      console.log("FILTER CHECK:", {
        bookingId,
        statusFilter,
        queryStartDate,
        queryEndDate,
        verificationStatus,
        serviceBookingId: sBookingId,
        matchesId,
        matchesDate,
        matchesStatus,
        matchesVerification,
      });

      return matchesId && matchesDate && matchesStatus && matchesVerification;
    });
  }, [services, bookingFilter]);

  // ---------- LOADING / ERROR / EMPTY ----------
  if (bookingsLoading) {
    return (
      <div className="w-full flex justify-center items-center py-10">
        <Loader />
      </div>
    );
  }

  if (bookingsError) {
    return (
      <div className="w-full flex justify-center py-10">
        <p className="text-sm text-red-500 bg-red-50 border border-red-200 px-4 py-3 rounded-xl">
          Error loading bookings: {bookingsError.message}
        </p>
      </div>
    );
  }

  if (!services || services.length === 0) {
    return (
      <div className="w-full flex items-center justify-center py-16">
        <div className="max-w-md text-center bg-white rounded-2xl shadow border border-gray-200 px-6 py-8">
          <p className="text-sm text-gray-500 mb-2">Total Services</p>
          <p className="text-lg font-semibold text-gray-900">
            No services / bookings available for{" "}
            {employeeData?.fullName || "this employee"}
          </p>
        </div>
      </div>
    );
  }

  // ---------- RENDER ----------
  return (
    <div className="w-full">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
        {/* Filters (status, verification, date presets) */}
        <EmployeeBookingFilterBar
          currentFilter={bookingFilter}
          onFilterChange={onFilterChange}
        />

        {/* Header + Booking ID filter */}
        <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-gray-900 text-lg font-semibold">
                Total Services
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Booking history for{" "}
                <span className="text-[#7F58E6] font-medium">
                  {employeeData?.fullName || "Employee"}
                </span>
              </p>
            </div>

            <div className="flex flex-col items-start sm:items-end gap-2">
              <p className="text-xs text-gray-500">
                Showing{" "}
                <span className="text-[#7F58E6] font-semibold">
                  {filteredServices.length}
                </span>{" "}
                of {services.length} bookings
              </p>
              {/* Booking ID filter */}
              <div className="w-full sm:w-64">
                <input
                  type="text"
                  value={bookingIdFilter}
                  onChange={handleBookingIdChange}
                  placeholder="Filter by Booking ID..."
                  className="w-full px-3 py-2 text-xs sm:text-sm rounded-lg bg-gray-100 border border-gray-300 text-gray-900 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7F58E6]"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Booking cards */}
        <div className="p-4 sm:p-6 space-y-3">
          {filteredServices.map((service) => (
            <div
              key={service.id}
              className="bg-white border border-gray-200 rounded-2xl overflow-hidden"
            >
              {/* Header: clickable row */}
              <button
                type="button"
                className="w-full text-left px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-100 transition-colors"
                onClick={() => toggleBooking(service.id)}
              >
                <div className="flex flex-wrap gap-4 justify-between text-xs sm:text-sm text-gray-700">
                  <div className="min-w-[140px]">
                    <p className="text-[11px] text-gray-500">Booking ID</p>
                    <p className="font-semibold break-all">
                      {service.bookingId}
                    </p>
                  </div>

                  <div className="min-w-[160px]">
                    <p className="text-[11px] text-gray-500">Date & Slot</p>
                    <p className="font-semibold">
                      {service.bookingDate} {service.timeSlot}
                    </p>
                  </div>

                  <div className="min-w-[120px]">
                    <p className="text-[11px] text-gray-500">Status</p>
                    <p className="font-semibold">{service.status}</p>
                  </div>

                  <div className="min-w-[160px]">
                    <p className="text-[11px] text-gray-500">Payment</p>
                    <p className="font-semibold">
                      {service.paymentStatus} ({service.paymentType})
                    </p>
                  </div>

                  <div className="min-w-[100px]">
                    <p className="text-[11px] text-gray-500">Total</p>
                    <p className="font-semibold">₹{service.price}</p>
                  </div>

                  <div className="min-w-[100px]">
                    <p className="text-[11px] text-gray-500">Type</p>
                    <p className="font-semibold">{service.bookingType}</p>
                  </div>
                </div>
              </button>

              {/* Expanded body */}
              {openBookingId === service.id && (
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-gray-200 text-xs sm:text-sm text-gray-700">
                  {/* Address + basic info */}
                  <div className="mt-3 space-y-1">
                    <p>
                      <span className="font-semibold">Address:&nbsp;</span>
                      <span className="whitespace-pre-wrap break-words">
                        {service.address}
                      </span>
                    </p>
                    <p>
                      <span className="font-semibold">Pincode:&nbsp;</span>
                      {service.pincode}
                    </p>
                    {service.reason && (
                      <p>
                        <span className="font-semibold">Reason:&nbsp;</span>
                        {service.reason}
                      </p>
                    )}
                    <p>
                      <span className="font-semibold">Customer:&nbsp;</span>
                      {service.user?.fullName || ""}
                      {service.user?.contactNumber &&
                        ` (${service.user.contactNumber})`}
                    </p>
                  </div>

                  {/* Timeline */}
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                    {[
                      ["Confirmed", service.confirmedAt],
                      ["Headed", service.headedToLocation],
                      ["Reached", service.reachedLocation],
                      ["Started", service.serviceStarted],
                      ["Finished", service.serviceFinished],
                      ["Cancelled", service.cancelledAt],
                    ].map(([label, val]) => (
                      <div
                        key={label}
                        className="bg-gray-100 rounded-lg px-3 py-2 border border-gray-200"
                      >
                        <p className="text-[10px] text-gray-500">{label}</p>
                        <p className="text-[11px] font-semibold text-gray-100">
                          {formatTime(val)}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Order details */}
                  <div className="mt-4">
                    <p className="font-semibold mb-2">Order Details:</p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {(service.orderDetail || []).map((detail) => (
                        <div
                          key={detail.id}
                          className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2"
                        >
                          <p className="text-[11px] text-gray-500">Service</p>
                          <p className="font-semibold text-sm text-gray-100">
                            {detail.name}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-1">
                            Price:{" "}
                            <span className="text-gray-100">
                              ₹{detail.price}
                            </span>
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TotalServicesPanel;
